<template>
  <div class="all-users-activity-page">
    <div class="page-header">
      <h1>Учёт рабочего времени</h1>
      <p v-if="!isLogist" class="page-desc">Первый вход за день, время в статусе «Отошел», время залогинен. Промежутки «не в сети» и «отошел» — по кнопке «Подробно» (время по Минску).</p>
      <p v-else class="page-desc">Первый вход за день, время залогинен. Промежутки «не в сети» — по кнопке «Подробно» (время по Минску).</p>
    </div>

    <div class="controls">
      <div class="control-row">
        <label class="control-label">Период:</label>
        <select v-model="period" class="period-select" @change="onPeriodChange">
          <option value="day">День</option>
          <option value="week">Неделя</option>
          <option value="month">Месяц</option>
        </select>
        <template v-if="period === 'day'">
          <label class="control-label">Дата:</label>
          <input v-model="dateStr" type="date" class="date-input" @change="onDateChange" @input="onDateChange" />
        </template>
        <template v-else>
          <span class="period-range">{{ periodRangeLabel }}</span>
        </template>
        <button type="button" class="btn-load" @click="fetchActivity">Загрузить</button>
        <span class="control-hint">Строк на странице:</span>
        <select v-model.number="perPage" class="per-page-select" @change="fetchActivity">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading">Загрузка…</div>
    <div v-else class="table-wrap">
      <table class="activity-table">
        <thead>
          <tr>
            <th v-if="byDay">Дата</th>
            <th>Пользователь</th>
            <th>Группа</th>
            <th>Первый вход</th>
            <th v-if="!isLogist">Отошел</th>
            <th>Залогинен</th>
            <th v-if="period === 'day'">Статус</th>
            <th>Промежутки</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(u, idx) in users" :key="byDay ? (u.date + '-' + u.id + '-' + idx) : u.id" :class="{ 'row-inactive': u.status === 'inactive' }">
            <td v-if="byDay">{{ formatDate(u.date) }}</td>
            <td>{{ u.label }}</td>
            <td>{{ u.group_name || '—' }}</td>
            <td>{{ formatFirstLogin(u.first_login_at) }}</td>
            <td v-if="!isLogist">{{ formatDuration(u.total_away_seconds_today) }}</td>
            <td>{{ formatDuration(u.total_logged_in_seconds_today) }}</td>
            <td v-if="period === 'day'">
              <span :class="['status-badge', 'status-' + u.status]">{{ statusLabel(u.status) }}</span>
            </td>
            <td class="cell-timeline">
              <button type="button" class="btn-detail" @click="openTimeline(u)">Подробно</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="timelineModal" class="modal-overlay" @click.self="closeTimeline">
      <div class="modal-box">
        <div class="modal-head">
          <h2>{{ timelineModal.label }}</h2>
          <button type="button" class="modal-close" @click="closeTimeline">×</button>
        </div>
        <p v-if="timelineModal.date" class="modal-sub">Дата: {{ formatDate(timelineModal.date) }}</p>
        <div v-if="timelineSegments.length" class="timeline-list">
          <div v-for="(s, i) in timelineSegments" :key="i" :class="['timeline-row', 'timeline-' + s.kind]">
            <strong>{{ timelineSegmentLabel(s) }}</strong>
            <span class="timeline-time">с {{ s.from_time }} по {{ s.to_time }}</span>
          </div>
        </div>
        <p v-else class="modal-empty">{{ isLogist ? 'Нет записанных промежутков за этот день (данные появятся после обновления сервера и новых событий входа/выхода).' : 'Нет записанных промежутков за этот день (данные появятся после обновления сервера и новых событий входа/выхода/«отошел»).' }}</p>
        <details v-if="timelineModal.timeline_events && timelineModal.timeline_events.length" class="raw-events">
          <summary>Технический лог (как хранится в базе)</summary>
          <pre>{{ JSON.stringify(timelineModal.timeline_events, null, 2) }}</pre>
        </details>
      </div>
    </div>

    <div v-if="!loading && total > 0" class="pagination">
      <span class="pagination-info">Всего: {{ total }}. Страница {{ page }} из {{ totalPages }}.</span>
      <button class="pagination-btn" :disabled="page <= 1" @click="goPage(1)">«</button>
      <button class="pagination-btn" :disabled="page <= 1" @click="goPage(page - 1)">‹</button>
      <span class="pagination-current">{{ page }}</span>
      <button class="pagination-btn" :disabled="page >= totalPages" @click="goPage(page + 1)">›</button>
      <button class="pagination-btn" :disabled="page >= totalPages" @click="goPage(totalPages)">»</button>
    </div>
  </div>
</template>

<script>
const API_BASE = 'https://manager.1akb.by';

export default {
  name: 'AllUsersActivityPage',
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      users: [],
      loading: true,
      tick: 0,
      period: 'day',
      dateStr: today,
      page: 1,
      perPage: 25,
      total: 0,
      byDay: false,
      timelineModal: null
    };
  },
  computed: {
    isLogist() {
      const u = this.$store.state.user;
      const role = (u && u.role) || '';
      const r = String(role).toLowerCase();
      return role && (r.includes('логист') || r === 'logist');
    },
    timelineSegments() {
      if (!this.timelineModal || !this.timelineModal.timeline_segments) return [];
      return this.timelineModal.timeline_segments;
    },
    totalPages() {
      if (this.total <= 0 || this.perPage <= 0) return 1;
      return Math.ceil(this.total / this.perPage);
    },
    periodRangeLabel() {
      const today = new Date();
      if (this.period === 'week') {
        const d = new Date(today);
        d.setDate(d.getDate() - 6);
        return `${d.toLocaleDateString('ru-RU')} — ${today.toLocaleDateString('ru-RU')}`;
      }
      if (this.period === 'month') {
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        return `${d.toLocaleDateString('ru-RU')} — ${today.toLocaleDateString('ru-RU')}`;
      }
      return '';
    }
  },
  mounted() {
    this.fetchActivity();
    this.refreshInterval = setInterval(this.fetchActivity, 60 * 1000);
    this.tickInterval = setInterval(() => { this.tick = Date.now(); }, 15000);
  },
  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    if (this.tickInterval) clearInterval(this.tickInterval);
  },
  methods: {
    statusLabel(s) {
      if (this.isLogist && s === 'away') return 'Не онлайн';
      return { online: 'Онлайн', away: 'Отошел', inactive: 'Не заходил' }[s] || s;
    },
    timelineSegmentLabel(s) {
      const label = (s && s.label) || '';
      if (!this.isLogist) return label;
      return String(label)
        .replace(/Отошел/g, 'Не онлайн')
        .replace(/отошел/gi, 'не в сети');
    },
    formatFirstLogin(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
    formatDuration(seconds) {
      if (seconds == null || seconds < 0) return '—';
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      if (h > 0) return `${h} ч ${m} мин`;
      return `${m} мин`;
    },
    formatDate(ymd) {
      if (!ymd) return '—';
      const d = new Date(ymd + 'T12:00:00Z');
      return d.toLocaleDateString('ru-RU');
    },
    onDateChange() {
      this.page = 1;
      this.fetchActivity();
    },
    onPeriodChange() {
      this.page = 1;
      this.fetchActivity();
    },
    openTimeline(u) {
      this.timelineModal = { ...u };
    },
    closeTimeline() {
      this.timelineModal = null;
    },
    goPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.fetchActivity();
    },
    async fetchActivity() {
      this.loading = true;
      const today = new Date().toISOString().slice(0, 10);
      try {
        const params = new URLSearchParams();
        params.set('page', String(this.page));
        params.set('per_page', String(this.perPage));
        if (this.period === 'day') {
          const date = (this.dateStr && this.dateStr.trim()) ? this.dateStr.trim() : today;
          params.set('date', date);
        } else if (this.period === 'week') {
          const d = new Date();
          d.setDate(d.getDate() - 6);
          params.set('date_from', d.toISOString().slice(0, 10));
          params.set('date_to', today);
        } else if (this.period === 'month') {
          const d = new Date();
          d.setDate(1);
          params.set('date_from', d.toISOString().slice(0, 10));
          params.set('date_to', today);
        }
        const res = await fetch(`${API_BASE}/all-users-activity?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          const isArray = Array.isArray(data);
          const fullList = isArray ? data : (data.items || []);
          const total = isArray ? fullList.length : (data.total != null ? data.total : fullList.length);
          if (isArray && fullList.length > 0) {
            const from = (this.page - 1) * this.perPage;
            this.users = fullList.slice(from, from + this.perPage);
          } else {
            this.users = fullList;
          }
          this.total = total;
          this.byDay = !!(data.by_day);
          if (!isArray) this.page = data.page != null ? data.page : 1;
        } else {
          this.users = [];
          this.total = 0;
          this.byDay = false;
        }
      } catch (e) {
        this.users = [];
        this.total = 0;
        this.byDay = false;
      }
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.all-users-activity-page {
  padding: 20px;
  max-width: 1000px;
}
.page-header {
  margin-bottom: 16px;
}
.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
}
.page-desc {
  margin: 0;
  color: #666;
  font-size: 14px;
}
.controls {
  margin-bottom: 16px;
}
.control-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.control-label {
  font-weight: 500;
}
.period-select {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}
.period-range {
  font-size: 13px;
  color: #444;
}
.date-input {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}
.control-hint {
  margin-left: 16px;
  color: #666;
  font-size: 13px;
}
.btn-load {
  padding: 6px 14px;
  border: 1px solid #555;
  background: #555;
  color: #fff;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}
.btn-load:hover {
  background: #333;
}
.per-page-select {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}
.loading {
  padding: 20px;
  color: #666;
}
.table-wrap {
  overflow-x: auto;
}
.activity-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.activity-table th,
.activity-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}
.activity-table th {
  background: #f5f5f5;
  font-weight: 600;
}
.row-inactive {
  color: #888;
}
.row-inactive td {
  font-style: italic;
}
.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}
.status-online {
  background: #d4edda;
  color: #155724;
}
.status-away {
  background: #fff3cd;
  color: #856404;
}
.status-inactive {
  background: #e2e3e5;
  color: #6c757d;
}
.pagination {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.pagination-info {
  margin-right: 12px;
  font-size: 13px;
  color: #666;
}
.pagination-btn {
  padding: 6px 10px;
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.pagination-btn:hover:not(:disabled) {
  background: #f0f0f0;
}
.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pagination-current {
  padding: 0 8px;
  font-weight: 600;
}
.btn-detail {
  padding: 4px 10px;
  font-size: 12px;
  border: 1px solid #0d6efd;
  background: #fff;
  color: #0d6efd;
  border-radius: 4px;
  cursor: pointer;
}
.btn-detail:hover {
  background: #e7f1ff;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.modal-box {
  background: #fff;
  border-radius: 8px;
  max-width: 520px;
  width: 100%;
  max-height: 85vh;
  overflow: auto;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.modal-head h2 {
  margin: 0;
  font-size: 1.15rem;
}
.modal-close {
  border: none;
  background: none;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  color: #666;
}
.modal-sub {
  margin: 8px 0 16px;
  color: #666;
  font-size: 14px;
}
.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.timeline-row {
  padding: 10px 12px;
  border-radius: 6px;
  border-left: 4px solid #ccc;
  background: #f9f9f9;
}
.timeline-offline {
  border-left-color: #6c757d;
}
.timeline-away {
  border-left-color: #ffc107;
}
.timeline-time {
  display: block;
  margin-top: 4px;
  font-size: 14px;
  color: #333;
}
.modal-empty {
  color: #666;
  font-size: 14px;
}
.raw-events {
  margin-top: 16px;
  font-size: 13px;
}
.raw-events pre {
  margin-top: 8px;
  padding: 10px;
  background: #f4f4f4;
  border-radius: 4px;
  overflow: auto;
  max-height: 200px;
  font-size: 11px;
}
</style>
